// MailboxHealthPage — /hub/admin/mailboxes (Epic C5 + D1).
//
// An ADMIN surface, not a page-affecting feature. Shows every connected mailbox
// (ops_mailboxes): its on/off state + rule, its sync health (v_mailbox_health —
// last sync, errors, counts), and its backfill progress (v_mailbox_chunks).
//
// Enable/disable write through fn_mailbox_enable / fn_mailbox_disable — keyed on
// the mailbox EMAIL ADDRESS (not a uuid — ops_mailboxes has no surrogate key).
// fn_mailbox_enable returns the Gmail query it will run; the page shows it to
// the admin so they can see what a personal mailbox is about to ingest (§C-1).
// fn_mailbox_disable requires a reason (p_reason — no default); the page collects
// it in a dialog before the write fires. Both writes are never retried, optimistic
// with a revert on error.
//
// Gated by the agent.manage capability (Brian and Aly — knowledge-base §14).
// The closest existing admin capability; a backend mailbox.manage capability is
// the eventual right gate (docs/CLIENTHUB-RPC-GAPS.md §7). Until it exists, the
// page renders <NotPermitted> for anyone without agent.manage — the wall is the
// capability check here, and the database still resolves auth.uid() on every
// write (00_CONTRACT.md §6).
//
// Column readers fall back across plausible names (mirrors InboxDrawer's norm);
// a missing field simply does not render. A thrown load/toggle is a real error
// -> <ErrorState> with a retry (knowledge-base §18: a missing/timing-out object
// is a backend bug to report, never a UI patch).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { HubShell } from "../../components/hub/shell";
import { NotPermitted } from "../../components/hub/kit";
import { EmptyState, ErrorState } from "../../components/shared/StateMessage";
import PanelErrorBoundary from "../../components/shared/PanelErrorBoundary";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { fmtDate } from "../../components/shared/format";
import {
  getMailboxes,
  getMailboxHealth,
  getMailboxChunks,
  enableMailbox,
  disableMailbox,
} from "../../api/conversations";

// Collapse the UNCONFIRMED column variants onto one shape. A missing field
// degrades to null/0 rather than crashing; when the probe confirms the real
// columns, collapse each reader to the single true key.
const mbIdOf = (m) => m?.id ?? m?.mailbox_id ?? m?.uuid ?? null;
const mbEmailOf = (m) => m?.email ?? m?.address ?? m?.email_address ?? null;
const mbLabelOf = (m) => m?.label ?? m?.name ?? m?.email ?? m?.address ?? "—";
const mbEnabledOf = (m) => m?.enabled ?? m?.is_enabled ?? m?.active ?? false;
const mbRuleOf = (m) => m?.rule ?? m?.routing_rule ?? m?.note ?? null;

const healthOf = (rows, id) =>
  rows.find((h) => (h?.mailbox_id ?? h?.id) === id) || null;
const chunksOf = (rows, id) =>
  rows.find((c) => (c?.mailbox_id ?? c?.id) === id) || null;

export default function MailboxHealthPage() {
  const { can } = useWorkspace();
  const permitted = can("agent.manage");

  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [mailboxes, setMailboxes] = useState([]);
  const [health, setHealth] = useState([]);
  const [chunks, setChunks] = useState([]);
  const [toggling, setToggling] = useState({}); // email -> boolean
  const [toggleErrors, setToggleErrors] = useState({}); // email -> message
  // Dialog state: null | { kind: 'enable' | 'disable', mailbox }
  const [pending, setPending] = useState(null);
  // Gmail query returned by fn_mailbox_enable (shown after the write).
  const [gmailQuery, setGmailQuery] = useState(null);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    setStatus("loading");
    setToggleErrors({});
    try {
      const [mbRows, healthRows, chunkRows] = await Promise.all([
        getMailboxes(),
        getMailboxHealth().catch(() => []),
        getMailboxChunks().catch(() => []),
      ]);
      setMailboxes(Array.isArray(mbRows) ? mbRows : []);
      setHealth(Array.isArray(healthRows) ? healthRows : []);
      setChunks(Array.isArray(chunkRows) ? chunkRows : []);
      setStatus("ready");
    } catch (err) {
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    if (permitted) load();
  }, [permitted, load]);

  // Clicking a toggle opens a dialog instead of writing immediately.
  // Enable → confirm dialog → write → show the returned Gmail query.
  // Disable → reason dialog (required text) → write.
  const onRequestToggle = useCallback(
    (mailbox) => {
      const email = mbEmailOf(mailbox);
      if (!email || toggling[email]) return;
      const nextEnabled = !mbEnabledOf(mailbox);
      setReason("");
      setPending({ kind: nextEnabled ? "enable" : "disable", mailbox });
    },
    [toggling],
  );

  const onConfirmToggle = useCallback(async () => {
    const mailbox = pending?.mailbox;
    if (!mailbox) return;
    const email = mbEmailOf(mailbox);
    const id = mbIdOf(mailbox);
    const nextEnabled = pending.kind === "enable";
    setPending(null);
    setToggling((p) => ({ ...p, [email]: true }));
    setToggleErrors((p) => ({ ...p, [email]: null }));
    // Optimistic: flip the row immediately.
    setMailboxes((prev) =>
      prev.map((m) =>
        mbEmailOf(m) === email ? { ...m, enabled: nextEnabled } : m,
      ),
    );
    try {
      if (nextEnabled) {
        const result = await enableMailbox(email);
        // fn_mailbox_enable returns the Gmail query it will run — show it
        // so the admin can see what the mailbox is about to ingest (§C-1).
        const query =
          result?.gmail_query ?? result?.query ?? result?.gmailQuery ?? null;
        if (query) setGmailQuery({ email, query });
      } else {
        await disableMailbox(email, reason);
      }
    } catch (err) {
      // Revert + surface the real error inline (never a silent swallow).
      setMailboxes((prev) =>
        prev.map((m) =>
          mbEmailOf(m) === email ? { ...m, enabled: !nextEnabled } : m,
        ),
      );
      setToggleErrors((p) => ({
        ...p,
        [email]: err?.message || "Couldn't update this mailbox.",
      }));
    } finally {
      setToggling((p) => ({ ...p, [email]: false }));
      setReason("");
    }
  }, [pending, reason]);

  const rows = useMemo(() => {
    if (status !== "ready") return [];
    return mailboxes.map((m) => {
      const id = mbIdOf(m);
      return {
        id,
        email: mbEmailOf(m),
        label: mbLabelOf(m),
        enabled: mbEnabledOf(m),
        rule: mbRuleOf(m),
        health: healthOf(health, id),
        chunks: chunksOf(chunks, id),
      };
    });
  }, [status, mailboxes, health, chunks]);

  if (!permitted) {
    return (
      <HubShell>
        <div className="p-6">
          <NotPermitted title="Not permitted" message="Mailbox health is an admin surface." />
        </div>
      </HubShell>
    );
  }

  return (
    <HubShell>
      <div className="mx-auto w-full max-w-5xl px-4 py-3 dark:border-gray-800 md:px-6 lg:px-10">
        <header className="mb-4 flex items-center gap-3">
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Mailbox health
          </h1>
          {status === "ready" && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {mailboxes.length} {mailboxes.length === 1 ? "mailbox" : "mailboxes"}
            </span>
          )}
        </header>

        {status === "loading" && (
          <div className="space-y-2" role="status" aria-label="Loading">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-16 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-900"
              />
            ))}
          </div>
        )}

        {status === "error" && (
          <ErrorState
            variant="card"
            message="This view is temporarily unavailable. Please try again shortly."
            onRetry={load}
          />
        )}

        {status === "ready" && (
          <PanelErrorBoundary label="mailbox-health">
            {rows.length === 0 ? (
              <EmptyState variant="card" message="No mailboxes are connected yet." />
            ) : (
              <ul className="space-y-2" data-testid="mailbox-list">
                {rows.map((r) => (
                  <MailboxRow
                    key={r.id}
                    row={r}
                    toggling={!!toggling[r.email]}
                    error={toggleErrors[r.email]}
                    onToggle={() =>
                      onRequestToggle(
                        mailboxes.find((m) => mbIdOf(m) === r.id),
                      )
                    }
                  />
                ))}
              </ul>
            )}
          </PanelErrorBoundary>
        )}

        {/* Enable confirm dialog */}
        {pending?.kind === "enable" && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div
              className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-5 shadow-xl dark:border-gray-800 dark:bg-gray-900"
              data-testid="mailbox-enable-dialog"
            >
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Enable {mbLabelOf(pending.mailbox)}?
              </h2>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {mbEmailOf(pending.mailbox)} will start ingesting email for every
                client domain. You will see the Gmail query it runs after enabling.
              </p>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setPending(null)}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                  data-testid="mailbox-enable-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onConfirmToggle}
                  className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700"
                  data-testid="mailbox-enable-confirm"
                >
                  Enable
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Disable reason dialog (p_reason is required) */}
        {pending?.kind === "disable" && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div
              className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-5 shadow-xl dark:border-gray-800 dark:bg-gray-900"
              data-testid="mailbox-disable-dialog"
            >
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Disable {mbLabelOf(pending.mailbox)}?
              </h2>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                A reason is required — it is the first question the next person
                asks.
              </p>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why are you disabling this mailbox?"
                className="mt-3 w-full rounded-md border border-gray-300 p-2 text-xs text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                rows={3}
                data-testid="mailbox-disable-reason"
              />
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => { setPending(null); setReason(""); }}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                  data-testid="mailbox-disable-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onConfirmToggle}
                  disabled={!reason.trim()}
                  className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
                  data-testid="mailbox-disable-confirm"
                >
                  Disable
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Gmail query result (returned by fn_mailbox_enable — §C-1) */}
        {gmailQuery && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div
              className="w-full max-w-lg rounded-lg border border-gray-200 bg-white p-5 shadow-xl dark:border-gray-800 dark:bg-gray-900"
              data-testid="gmail-query-dialog"
            >
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {gmailQuery.email} is now enabled
              </h2>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                It will ingest mail matching this Gmail query:
              </p>
              <pre
                className="mt-3 max-h-48 overflow-auto rounded-md bg-gray-100 p-3 text-xs text-gray-800 dark:bg-gray-800 dark:text-gray-200"
                data-testid="gmail-query-text"
              >
                {gmailQuery.query}
              </pre>
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => setGmailQuery(null)}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                  data-testid="gmail-query-close"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </HubShell>
  );
}

function MailboxRow({ row, toggling, error, onToggle }) {
  const h = row.health || {};
  const c = row.chunks || null;
  const errorsToday = h.errors_today ?? h.errors ?? 0;
  const hasError = !!h.last_error || errorsToday > 0;
  return (
    <li
      className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-gray-900"
      data-testid={`mailbox-row-${row.id}`}
      data-mailbox-id={row.id}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {row.label}
          </p>
          {row.email && (
            <p className="text-xs text-gray-500 dark:text-gray-400">{row.email}</p>
          )}
          {row.rule && (
            <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
              Rule: {row.rule}
            </p>
          )}
        </div>

        <div className="flex items-center gap-3">
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
              row.enabled
                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                : "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
            }`}
            data-testid={`mailbox-state-${row.id}`}
          >
            {row.enabled ? "On" : "Off"}
          </span>
          <button
            type="button"
            onClick={onToggle}
            disabled={toggling}
            className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
            data-testid={`mailbox-toggle-${row.id}`}
            aria-pressed={row.enabled}
          >
            {toggling ? "…" : row.enabled ? "Disable" : "Enable"}
          </button>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
        {h.last_sync_at && (
          <span>Last sync {fmtDate(h.last_sync_at)}</span>
        )}
        <span>{h.messages_today ?? h.messages ?? 0} today</span>
        <span
          className={
            hasError ? "font-medium text-amber-700 dark:text-amber-400" : ""
          }
        >
          {errorsToday} errors
        </span>
        {h.pending != null && <span>{h.pending} pending</span>}
        {h.last_error && (
          <span className="text-amber-700 dark:text-amber-400">
            {h.last_error}
          </span>
        )}
      </div>

      {c && (
        <div
          className="mt-2 text-xs text-gray-500 dark:text-gray-400"
          data-testid={`mailbox-chunks-${row.id}`}
        >
          Backfill {c.synced_chunks ?? 0}/{c.total_chunks ?? 0}
          {c.estimated_done_at ? ` · est. done ${fmtDate(c.estimated_done_at)}` : ""}
        </div>
      )}

      {error && (
        <div className="mt-2">
          <ErrorState variant="strip" message={error} />
        </div>
      )}
    </li>
  );
}

